import type { AxiosInstance } from "axios";
import { K8sResourceProxy } from "./cluster-resource-proxy";
import { IngressClassItemSchema, ServiceItemSchema, type IngressClassItem } from "../schema/k8s-resource/ingress";
import { axiosInstance } from "@kce2/app/axios";

/** Controller LB IP 获取失败原因（见 CONTEXT.md） */
export type FailureReason =
  | "no-controller-annotation"
  | "unsupported-controller"
  | "service-not-found"
  | "lb-not-ready"
  | "request-failed";

/** getServiceIp 返回类型 */
export interface ServiceIpResult {
  ip: string | null;
  errorReason?: FailureReason;
}

/** resolveControllerService 返回类型 */
export interface ResolveResult {
  name: string;
  namespace: string;
}

/**
 * Ingress/IngressClass 相关 API。
 *
 * 组合 K8sResourceProxy（不继承共享基类，见 docs/adr/0001），
 * 在其上封装 IngressClass 列表与 Controller Service LB IP 解析。
 *
 * apiVersion/resourceType 作为方法参数带默认值，避免将来为不同 K8s 版本资源新建 class。
 */
export class IngressApi {
  private proxy: K8sResourceProxy;

  constructor(axios: AxiosInstance = axiosInstance) {
    this.proxy = new K8sResourceProxy(axios);
  }

  /** 列出集群全部 IngressClass（集群级资源，无 namespace） */
  listIngressClass(params: { region: string; clusterId: string }) {
    return this.proxy.listResources({
      ...params,
      apiVersion: "networking.k8s.io/v1",
      resourceType: "ingressclasses",
      itemSchema: IngressClassItemSchema,
    });
  }

  /**
   * 由 IngressClass 派生 Controller Service 的 { name, namespace }。
   *
   * 编码 ingress-nginx 约定（见 docs/adr/0002）：
   * - name = `${ingressClassName}-controller`
   * - namespace = annotations["meta.helm.sh/release-namespace"]
   *
   * 不门控 spec.controller：对所有 IngressClass 都按约定尝试派生，
   * 查不到 Service 时由 getServiceIp 自然降级为 service-not-found。
   */
  resolveControllerService(ingressClass: IngressClassItem): {
    service: ResolveResult | null;
    errorReason?: FailureReason;
  } {
    const name = ingressClass.metadata?.name;
    if (!name) {
      return { service: null, errorReason: "unsupported-controller" };
    }

    const namespace = ingressClass.metadata?.annotations?.["meta.helm.sh/release-namespace"];
    if (!namespace) {
      return { service: null, errorReason: "no-controller-annotation" };
    }

    return { service: { name: `${name}-controller`, namespace } };
  }

  /**
   * 解析 IngressClass 的 Controller Service LB ExternalIP。
   *
   * 多跳流程（见 CONTEXT.md）：
   * resolveControllerService → getResource(Service, ${name}-controller) → status.loadBalancer.ingress[].ip
   *
   * 任一跳失败返回 { ip: null } + 对应 FailureReason。
   */
  async getServiceIp(params: {
    region: string;
    clusterId: string;
    ingressClass: IngressClassItem;
  }): Promise<ServiceIpResult> {
    const { region, clusterId, ingressClass } = params;

    const { service, errorReason } = this.resolveControllerService(ingressClass);
    if (!service) {
      return { ip: null, errorReason };
    }

    let serviceRes;
    try {
      serviceRes = await this.proxy.getResource({
        region,
        clusterId,
        apiVersion: "v1",
        resourceType: "services",
        name: service.name,
        namespace: service.namespace,
        itemSchema: ServiceItemSchema,
      });
    } catch {
      return { ip: null, errorReason: "request-failed" };
    }

    const data = serviceRes.data?.Data;
    if (!data) {
      return { ip: null, errorReason: "service-not-found" };
    }

    const ingress = data.status?.loadBalancer?.ingress;
    const ip = ingress?.find((entry: { ip?: string }) => entry.ip)?.ip;
    if (!ip) {
      return { ip: null, errorReason: "lb-not-ready" };
    }

    return { ip };
  }
}

export const ingressApi = new IngressApi();
